/* eslint-disable @woovi/no-hardcoded-font-weights */
import { formatToCPFOrCNPJ } from 'brazilian-values';
import moment from 'moment';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import {
  Document,
  Page,
  Stack,
  StyleSheet,
  Typography,
  Box,
  LogoWoovi,
  LogoOpenPix,
} from '@woovi/pdf';
import { COMPANY_PROJECT_TYPES_ENUM } from '@woovi/roles';
import { convertToMoney } from '@woovi/utils';

import type { ChargePDFDocument_charge$key } from './__generated__/ChargePDFDocument_charge.graphql.ts';
import { ChargePDFDocumentBoleto } from './ChargePDFDocumentBoleto.tsx';
import { ChargePDFDocumentPix } from './ChargePDFDocumentPix.tsx';
import { useChargePaymentMethod } from '../../../../charge/useChargePaymentMethod.tsx';

const styles = StyleSheet.create({
  heading: {
    marginTop: 25,
  },
  page: {
    padding: '32 20 52 20',
  },
  label: {
    fontSize: 8,
    textAlign: 'center',
  },
  labelSmall: {
    fontSize: 8,
    display: 'flex',
    flexWrap: 'wrap',
  },
  barCode: {
    marginTop: 10,
    maxWidth: 344,
  },
  leftColumn: {
    flexDirection: 'column',
    gap: '4px',
    padding: '4px 6px',
    width: 390,
  },
  rightColumn: {
    flexDirection: 'column',
    gap: '4px',
    borderLeft: '1px solid black',
    padding: '4px 6px',
    height: '100%',
  },
  rightColumnStacked: {
    flexDirection: 'column',
    gap: '4px',
    padding: '4px 6px',
  },
  valueSmall: {
    fontSize: '10px',
    width: 152,
  },
  value: {
    fontSize: '10px',
    textTransform: 'uppercase',
  },
});

type ChargePDFDocumentProps = {
  charge: ChargePDFDocument_charge$key;
};

const ChargePDFDocument = (props: ChargePDFDocumentProps): ReactNode => {
  const { t } = useTranslation();

  const charge = useFragment<ChargePDFDocument_charge$key>(
    graphql`
      fragment ChargePDFDocument_charge on Charge {
        value
        brCode
        company {
          name
          nameFriendly
          projectType
          taxID {
            taxID
          }
        }
        customer {
          name
          taxID {
            taxID
          }
        }
        expiresDate
        createdAt
        comment
        identifier
        ...useChargePaymentMethod_charge
      }
    `,
    props.charge,
  );

  const { hasBoleto, hasPix, boleto } = useChargePaymentMethod(charge);

  const { company, customer: _ } = charge;

  const getBeneficiary = (): string => {
    const companyName = company?.nameFriendly || company?.name;

    const companyTaxID = company?.taxID?.taxID
      ? formatToCPFOrCNPJ(company.taxID.taxID)
      : '';

    return `${companyName} - ${companyTaxID}`;
  };

  const getPayer = (): string => {
    if (!charge?.customer) {
      return '';
    }

    const customerName = charge?.customer?.name;
    const customerTaxID = charge?.customer?.taxID?.taxID
      ? formatToCPFOrCNPJ(charge?.customer?.taxID.taxID)
      : '';

    return `${customerName} - ${customerTaxID}`;
  };

  const title =
    company?.projectType === COMPANY_PROJECT_TYPES_ENUM.WOOVI
      ? t('Invoice - Woovi')
      : t('Invoice - OpenPix');

  const subject =
    company?.projectType === COMPANY_PROJECT_TYPES_ENUM.WOOVI
      ? t('Woovi')
      : t('OpenPix');

  const author =
    company?.projectType === COMPANY_PROJECT_TYPES_ENUM.WOOVI
      ? 'woovi.com'
      : 'openpix.com';

  const getBoleto = (): ReactNode => {
    if (!hasBoleto) {
      return null;
    }

    return (
      <ChargePDFDocumentBoleto
        barcode={boleto.boletoBarcode || ''}
        digitable={boleto.boletoDigitable || ''}
        t={t}
      />
    );
  };

  const getPix = (): ReactNode => {
    if (!hasPix) {
      return null;
    }

    return <ChargePDFDocumentPix brCode={hasPix.brCode} t={t} />;
  };

  return (
    <Document
      author={author}
      creator={author}
      producer={author}
      subject={subject}
      title={title}
    >
      <Page style={styles.page} wrap>
        {getPix()}
        <Stack
          direction='row'
          style={{
            gap: 6,
            alignItems: 'baseline',
          }}
        >
          <Box style={{ width: 100, marginBottom: 8 }}>
            {company?.projectType === COMPANY_PROJECT_TYPES_ENUM.WOOVI ? (
              <LogoWoovi />
            ) : (
              <LogoOpenPix />
            )}
          </Box>
        </Stack>

        <Stack
          direction='row'
          style={{
            alignItems: 'baseline',
            border: '1px solid black',
          }}
        >
          <Box style={{ ...styles.leftColumn }}>
            <Typography style={styles.labelSmall}>
              {t('Local of payment')}
            </Typography>
            <Typography style={styles.value}>
              {t(
                'PAYABLE AT ANY BANK OR BANKING APPLICATION OF YOUR PREFERENCE.',
              )}
            </Typography>
          </Box>
          <Box style={styles.rightColumn}>
            <Typography style={styles.labelSmall}>{t('Expiration')}</Typography>
            <Typography
              style={{ ...styles.valueSmall, fontWeight: 'semibold' }}
            >
              {moment(charge.expiresDate).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction='row'
          style={{
            alignItems: 'baseline',
            border: '1px solid black',
            borderTop: 'none',
          }}
        >
          <Box style={styles.leftColumn}>
            <Typography style={styles.labelSmall}>Beneficiário</Typography>
            <Typography style={styles.value}>{getBeneficiary()}</Typography>
          </Box>
          <Box style={styles.rightColumn}>
            <Typography style={styles.labelSmall}>
              {t('Agency / Beneficiary Code')}
            </Typography>
            <Typography style={styles.valueSmall}>&nbsp;</Typography>
          </Box>
        </Stack>

        <Stack
          direction='row'
          style={{
            alignItems: 'baseline',
            border: '1px solid black',
            borderTop: 'none',
          }}
        >
          <Stack direction='row' style={{ height: '100%', width: 390 }}>
            <Stack
              style={{
                padding: '4px 6px',
              }}
            >
              <Typography style={styles.labelSmall}>
                {t('Date of Document')}
              </Typography>
              <Typography style={styles.value}>
                {moment(charge.createdAt).format('DD/MM/YYYY HH:mm')}
              </Typography>
            </Stack>

            <Stack
              style={{
                padding: '4px 6px',
                borderLeft: '1px solid black',
              }}
            >
              <Typography style={styles.labelSmall}>
                {t('Identifier Number')}
              </Typography>
              <Typography style={styles.value}>{charge.identifier}</Typography>
            </Stack>
          </Stack>

          <Box style={styles.rightColumn}>
            <Typography style={styles.labelSmall}>{t('Our Number')}</Typography>
            <Typography style={styles.valueSmall}>&nbsp;</Typography>
          </Box>
        </Stack>

        <Stack
          direction='row'
          style={{
            alignItems: 'baseline',
            border: '1px solid black',
            borderTop: 'none',
          }}
        >
          <Box style={{ ...styles.leftColumn }}>
            <Typography style={styles.labelSmall}>{t('Specie')}</Typography>
            <Typography style={styles.value}>REAL (R$)</Typography>
          </Box>
          <Box style={styles.rightColumn}>
            <Typography style={styles.labelSmall}>
              {t('Value of Document')}
            </Typography>
            <Typography style={styles.valueSmall}>
              {convertToMoney(charge.value)}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction='row'
          style={{
            alignItems: 'baseline',
            border: '1px solid black',
            borderTop: 'none',
            minHeight: '108px',
          }}
        >
          <Box style={{ ...styles.leftColumn }}>
            <Typography style={styles.labelSmall}>
              {t('Instructions (Text of responsibility of the beneficiary)')}
            </Typography>
            <Typography style={styles.value}>{charge.comment}</Typography>
          </Box>

          <Stack
            style={{
              borderLeft: '1px solid black',
              height: '100%',
            }}
          >
            <Box style={styles.rightColumnStacked}>
              <Typography style={styles.labelSmall}>
                {t('(-) Discount / Abatements')}
              </Typography>
              <Typography style={styles.valueSmall}>&nbsp;</Typography>
            </Box>

            <Box
              style={{
                ...styles.rightColumnStacked,
                borderTop: '1px solid black',
              }}
            >
              <Typography style={styles.labelSmall}>
                {t('(-) Other deductions')}
              </Typography>
              <Typography style={styles.valueSmall}>&nbsp;</Typography>
            </Box>

            <Box
              style={{
                ...styles.rightColumnStacked,
                borderTop: '1px solid black',
              }}
            >
              <Typography style={styles.labelSmall}>{t('(+) Tax')}</Typography>
              <Typography style={styles.valueSmall}>&nbsp;</Typography>
            </Box>

            <Box
              style={{
                ...styles.rightColumnStacked,
                borderTop: '1px solid black',
              }}
            >
              <Typography style={styles.labelSmall}>
                {t('(+) Other charges')}
              </Typography>
              <Typography style={styles.valueSmall}>&nbsp;</Typography>
            </Box>

            <Box
              style={{
                ...styles.rightColumnStacked,
                borderTop: '1px solid black',
              }}
            >
              <Typography style={styles.labelSmall}>
                {t('(=) Value paid')}
              </Typography>
              <Typography style={styles.valueSmall}>
                {convertToMoney(charge.value)}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Stack
          style={{
            gap: 6,
            border: '1px solid black',
            borderTop: 'none',
            padding: '4px 6px',
          }}
        >
          <Typography style={styles.labelSmall}>{t('Payer')}</Typography>
          <Typography style={styles.value}>{getPayer()}</Typography>
        </Stack>
        {getBoleto()}
      </Page>
    </Document>
  );
};

export default ChargePDFDocument;
